import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, LinearProgress, Chip, Button,
  CircularProgress, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, IconButton, Tooltip, Collapse, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Avatar,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Replay as RevertIcon,
  RestartAlt as ResetIcon,
  UploadFile as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InsertDriveFile as FileIcon,
  Warning as WarningIcon,
  PauseCircle as OnHoldIcon,
  RadioButtonUnchecked as NotStartedIcon,
  TrendingUp as InProgressIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ── Status config ─────────────────────────────────────────────────────────
const STATUS = {
  not_started: { color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', label: 'Not Started',  icon: <NotStartedIcon sx={{ fontSize: 15 }} /> },
  in_progress:  { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', label: 'In Progress',  icon: <InProgressIcon  sx={{ fontSize: 15 }} /> },
  completed:    { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', label: 'Completed',    icon: <CheckCircleIcon sx={{ fontSize: 15 }} /> },
  delayed:      { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Delayed',      icon: <WarningIcon     sx={{ fontSize: 15 }} /> },
  on_hold:      { color: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'On Hold',      icon: <OnHoldIcon      sx={{ fontSize: 15 }} /> },
};

const DELAY_REASONS = [
  { value: 'weather',          label: 'Weather' },
  { value: 'material',         label: 'Material Shortage' },
  { value: 'manpower',         label: 'Manpower' },
  { value: 'approval_pending', label: 'Approval Pending' },
  { value: 'client',           label: 'Client Side' },
  { value: 'other',            label: 'Other' },
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Decode HTML entities (sanitize middleware encodes & as &amp; etc.)
const decodeHtml = (str) => {
  if (!str) return str;
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
};
const today = new Date();

// ── Gantt bar helper ──────────────────────────────────────────────────────
const buildGanttScale = (milestones) => {
  const dated = milestones.filter(m => m.startDate && m.endDate);
  if (dated.length === 0) return null;
  const starts = dated.map(m => new Date(m.startDate));
  const ends   = dated.map(m => new Date(m.endDate));
  const minD = new Date(Math.min(...starts));
  const maxD = new Date(Math.max(...ends));
  // Add 5% padding
  const range = maxD - minD || 1;
  const pad = range * 0.03;
  return {
    min: new Date(minD - pad),
    max: new Date(maxD + pad),
    range: (maxD + pad) - (minD - pad),
  };
};

const pct = (date, scale) => Math.max(0, Math.min(100, ((new Date(date) - scale.min) / scale.range) * 100));

// ── Gantt chart ───────────────────────────────────────────────────────────
const GanttChart = ({ milestones }) => {
  const scale = buildGanttScale(milestones);
  if (!scale) return (
    <Box sx={{ py: 3, textAlign: 'center' }}>
      <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>
        Set start & end dates on milestones to see the Gantt chart
      </Typography>
    </Box>
  );

  const todayPct = pct(today, scale);
  const months = [];
  const cur = new Date(scale.min);
  cur.setDate(1);
  while (cur <= scale.max) {
    months.push({ label: cur.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }), pct: pct(cur, scale) });
    cur.setMonth(cur.getMonth() + 1);
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 600, position: 'relative', px: 1 }}>
        {/* Month labels */}
        <Box sx={{ position: 'relative', height: 22, mb: 0.5, ml: '30px' }}>
          {months.map((m, i) => (
            <Typography key={i} sx={{
              position: 'absolute', left: `${m.pct}%`,
              fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600,
              transform: 'translateX(-50%)', whiteSpace: 'nowrap',
            }}>{m.label}</Typography>
          ))}
        </Box>
        {/* Grid lines + today marker — overlaid on bar area only (skip the 30px label column) */}
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', left: 30, right: 0, top: 0, bottom: 0, zIndex: 0 }}>
            {months.map((m, i) => (
              <Box key={i} sx={{
                position: 'absolute', left: `${m.pct}%`, top: 0, bottom: 0,
                width: '1px', background: '#f1f5f9',
              }} />
            ))}
            {todayPct >= 0 && todayPct <= 100 && (
              <Box sx={{
                position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0,
                width: '2px', background: '#ef4444', zIndex: 2,
                '&::before': { content: '"today"', position: 'absolute', top: -18,
                  left: '50%', transform: 'translateX(-50%)',
                  fontSize: '0.6rem', color: '#ef4444', fontWeight: 700, whiteSpace: 'nowrap' }
              }} />
            )}
          </Box>
          {/* Milestone bars */}
          {milestones.map((m) => {
            const cfg = STATUS[m.status] || STATUS.not_started;
            if (!m.startDate || !m.endDate) return (
              <Box key={m.id} sx={{ height: 34, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: cfg.bg, border: `1.5px solid ${cfg.border}`, color: cfg.color, fontWeight: 800, fontSize: '0.65rem', flexShrink: 0 }}>
                  {m.milestoneNumber}
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>{decodeHtml(m.title)}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1', fontStyle: 'italic' }}>· no dates set</Typography>
              </Box>
            );
            const left = pct(m.startDate, scale);
            const right = pct(m.endDate, scale);
            const width = Math.max(right - left, 2);
            return (
              <Box key={m.id} sx={{ height: 34, mb: 1, position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: cfg.bg, border: `1.5px solid ${cfg.border}`, color: cfg.color, fontWeight: 800, fontSize: '0.65rem', flexShrink: 0, zIndex: 1 }}>
                  {m.status === 'completed' ? '✓' : m.milestoneNumber}
                </Box>
                <Box sx={{ position: 'relative', flex: 1, height: '100%' }}>
                  <Tooltip title={`${decodeHtml(m.title)} · ${fmtDate(m.startDate)} → ${fmtDate(m.endDate)} · ${m.completionPct || 0}%`}>
                    <Box sx={{
                      position: 'absolute', left: `${left}%`, width: `${width}%`,
                      height: 22, top: '50%', transform: 'translateY(-50%)',
                      borderRadius: '6px',
                      background: m.status === 'completed'
                        ? `linear-gradient(90deg, #16a34a, #22c55e)`
                        : m.status === 'delayed'
                        ? `linear-gradient(90deg, #dc2626, #ef4444)`
                        : m.status === 'in_progress'
                        ? `linear-gradient(90deg, #2563eb, #3b82f6)`
                        : m.status === 'on_hold'
                        ? `linear-gradient(90deg, #d97706, #f59e0b)`
                        : '#e2e8f0',
                      display: 'flex', alignItems: 'center', px: 1,
                      overflow: 'hidden', cursor: 'default',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      minWidth: 24,
                    }}>
                      <Typography sx={{ fontSize: '0.65rem', color: m.status === 'not_started' ? '#64748b' : 'white',
                        fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {decodeHtml(m.title)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// ── Progress ring ─────────────────────────────────────────────────────────
const ProgressRing = ({ pct: p }) => {
  const r = 38, circ = 2 * Math.PI * r;
  const color = p >= 100 ? '#16a34a' : p >= 60 ? '#2563eb' : p >= 30 ? '#d97706' : '#94a3b8';
  return (
    <Box sx={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
      <svg width="96" height="96" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="48" cy="48" r={r} fill="none" stroke="#f1f5f9" strokeWidth="9" />
        <circle cx="48" cy="48" r={r} fill="none" stroke={color} strokeWidth="9"
          strokeDasharray={circ} strokeDashoffset={circ - (p / 100) * circ}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color, lineHeight: 1 }}>{p}%</Typography>
        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600 }}>DONE</Typography>
      </Box>
    </Box>
  );
};

// ── Status chip ───────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const cfg = STATUS[status] || STATUS.not_started;
  return (
    <Chip size="small" icon={cfg.icon} label={cfg.label}
      sx={{ fontSize: '0.68rem', fontWeight: 700, height: 22,
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
        '& .MuiChip-icon': { color: cfg.color, ml: '6px' } }} />
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
const MilestonesTab = ({ siteId, siteName }) => {
  const { user } = useAuth();
  const isAdminOrManager = ['admin', 'manager'].includes(user?.role);
  const isAdmin = user?.role === 'admin';

  const [milestones, setMilestones] = useState([]);
  const [overallPct, setOverallPct]   = useState(0);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [saving, setSaving]           = useState(false);
  const [expandedId, setExpandedId]   = useState(null);
  const [view, setView]               = useState('table'); // 'table' | 'gantt'

  // Dialog states
  const [addDialog,      setAddDialog]      = useState(false);
  const [editDialog,     setEditDialog]     = useState({ open: false, milestone: null });
  const [completeDialog, setCompleteDialog] = useState({ open: false, milestone: null });
  const [deleteConfirm,  setDeleteConfirm]  = useState({ open: false, milestone: null });
  const [revertConfirm,  setRevertConfirm]  = useState({ open: false, milestone: null });
  const [resetConfirm,   setResetConfirm]   = useState(false);
  const [uploadDialog,   setUploadDialog]   = useState({ open: false, milestone: null });

  // Form state
  const emptyForm = { title: '', description: '', startDate: '', endDate: '', assignedTo: '', notes: '', delayReason: '', delayNotes: '', status: 'not_started', completionPct: 0 };
  const [form, setForm] = useState(emptyForm);
  const [completeNotes, setCompleteNotes] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef();

  // Site users for assignee dropdown
  const [siteUsers, setSiteUsers] = useState([]);

  const fetchMilestones = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/sites/${siteId}/milestones`, authHeaders());
      setMilestones(res.data.data.milestones);
      setOverallPct(res.data.data.overallProgress);
    } catch (e) { setError('Failed to load milestones'); }
    finally { setLoading(false); }
  }, [siteId]);

  const fetchSiteUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/sites/${siteId}`, authHeaders());
      setSiteUsers(res.data.data?.site?.assignedUsers || []);
    } catch {}
  }, [siteId]);

  useEffect(() => { fetchMilestones(); fetchSiteUsers(); }, [fetchMilestones, fetchSiteUsers]);

  const calcOverall = (ms) => {
    if (!ms.length) return 0;
    return Math.round(ms.reduce((s, m) => s + (m.status === 'completed' ? 100 : m.completionPct || 0), 0) / ms.length);
  };

  // ── Add milestone ────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/sites/${siteId}/milestones`, form, authHeaders());
      setAddDialog(false); setForm(emptyForm); fetchMilestones();
    } catch (e) { setError('Failed to add milestone'); }
    finally { setSaving(false); }
  };

  // ── Edit milestone ───────────────────────────────────────────────────────
  const handleEdit = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_BASE_URL}/sites/${siteId}/milestones/${editDialog.milestone.id}`, form, authHeaders());
      setEditDialog({ open: false, milestone: null }); setForm(emptyForm); fetchMilestones();
    } catch (e) { setError('Failed to update milestone'); }
    finally { setSaving(false); }
  };

  // ── Complete ─────────────────────────────────────────────────────────────
  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_BASE_URL}/sites/${siteId}/milestones/${completeDialog.milestone.id}/complete`,
        { notes: completeNotes }, authHeaders());
      setCompleteDialog({ open: false, milestone: null }); fetchMilestones();
    } catch (e) { setError('Failed to complete milestone'); }
    finally { setSaving(false); }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API_BASE_URL}/sites/${siteId}/milestones/${deleteConfirm.milestone.id}`, authHeaders());
      setDeleteConfirm({ open: false, milestone: null }); fetchMilestones();
    } catch (e) { setError('Failed to delete milestone'); }
    finally { setSaving(false); }
  };

  // ── Revert ───────────────────────────────────────────────────────────────
  const handleRevert = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_BASE_URL}/sites/${siteId}/milestones/${revertConfirm.milestone.id}/revert`, {}, authHeaders());
      setRevertConfirm({ open: false, milestone: null }); fetchMilestones();
    } catch (e) { setError('Failed to revert milestone'); }
    finally { setSaving(false); }
  };

  // ── Reset ────────────────────────────────────────────────────────────────
  const handleReset = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API_BASE_URL}/sites/${siteId}/milestones/reset`, authHeaders());
      setResetConfirm(false); fetchMilestones();
    } catch (e) { setError('Failed to reset milestones'); }
    finally { setSaving(false); }
  };

  // ── Upload document ──────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!uploadFile) return;
    setSaving(true);
    const fd = new FormData();
    fd.append('document', uploadFile);
    try {
      await axios.post(`${API_BASE_URL}/sites/${siteId}/milestones/${uploadDialog.milestone.id}/upload`,
        fd, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' } });
      setUploadDialog({ open: false, milestone: null }); setUploadFile(null); fetchMilestones();
    } catch (e) { setError('Upload failed'); }
    finally { setSaving(false); }
  };

  // ── Open edit dialog ─────────────────────────────────────────────────────
  const openEdit = (m) => {
    setForm({
      title: m.title || '', description: m.description || '',
      startDate: m.startDate || '', endDate: m.endDate || '',
      assignedTo: m.assignedTo || '', notes: m.notes || '',
      delayReason: m.delayReason || '', delayNotes: m.delayNotes || '',
      status: m.status || 'not_started', completionPct: m.completionPct || 0,
    });
    setEditDialog({ open: true, milestone: m });
  };

  // ── Summary counts ───────────────────────────────────────────────────────
  const counts = milestones.reduce((a, m) => { a[m.status] = (a[m.status] || 0) + 1; return a; }, {});

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Header ── */}
      <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', mb: 2.5, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <ProgressRing pct={overallPct} />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a' }}>
                {siteName} — Project Tracker
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.25 }}>
                {milestones.length} milestones · EV Bus Depot Template
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                {Object.entries(STATUS).map(([key, cfg]) => counts[key] ? (
                  <Chip key={key} size="small" label={`${counts[key]} ${cfg.label}`}
                    sx={{ fontSize: '0.65rem', fontWeight: 700, height: 20,
                      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }} />
                ) : null)}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* View toggle */}
            <Box sx={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              {['table', 'gantt'].map(v => (
                <Button key={v} size="small" onClick={() => setView(v)}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1.5, py: 0.5, borderRadius: 0,
                    background: view === v ? '#0f172a' : 'white', color: view === v ? 'white' : '#64748b',
                    '&:hover': { background: view === v ? '#0f172a' : '#f8fafc' } }}>
                  {v === 'table' ? '≡ Table' : '⊟ Gantt'}
                </Button>
              ))}
            </Box>
            <Button size="small" onClick={fetchMilestones} startIcon={<RefreshIcon />}
              sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '0.75rem', border: '1px solid #e2e8f0', color: '#64748b' }}>
              Refresh
            </Button>
            {isAdminOrManager && (
              <Button size="small" variant="contained" startIcon={<AddIcon />}
                onClick={() => { setForm(emptyForm); setAddDialog(true); }}
                sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.75rem',
                  background: 'linear-gradient(135deg,#0f172a,#1e293b)' }}>
                Add Milestone
              </Button>
            )}
            {isAdmin && (
              <Tooltip title="Reset to default template">
                <IconButton size="small" onClick={() => setResetConfirm(true)} sx={{ color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px' }}>
                  <ResetIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Overall progress bar */}
        <Box sx={{ mt: 2.5 }}>
          <LinearProgress variant="determinate" value={overallPct}
            sx={{ height: 8, borderRadius: 4, background: '#f1f5f9',
              '& .MuiLinearProgress-bar': { borderRadius: 4,
                background: overallPct >= 100 ? '#16a34a' : overallPct >= 60 ? '#2563eb' : '#d97706' } }} />
        </Box>
      </Paper>

      {/* ── Gantt view ── */}
      {view === 'gantt' && (
        <Paper sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', mb: 2.5, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 2 }}>Timeline</Typography>
          <GanttChart milestones={milestones} />
        </Paper>
      )}

      {/* ── Table view ── */}
      {view === 'table' && (
        <Paper sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#f8fafc' }}>
                  {['#', 'Milestone', 'Assigned', 'Start', 'End', 'Progress', 'Status', 'Doc', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#475569',
                      py: 1.5, whiteSpace: 'nowrap', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {milestones.map((m) => {
                  const cfg = STATUS[m.status] || STATUS.not_started;
                  const isExpanded = expandedId === m.id;
                  const isOverdue = m.endDate && new Date(m.endDate) < today && m.status !== 'completed';
                  return (
                    <React.Fragment key={m.id}>
                      <TableRow hover sx={{
                        cursor: 'pointer',
                        background: m.status === 'completed' ? '#fafffe' : 'white',
                        borderLeft: `3px solid ${cfg.color}`,
                        '&:hover': { background: '#f8fafc' },
                      }} onClick={() => setExpandedId(isExpanded ? null : m.id)}>

                        {/* # */}
                        <TableCell sx={{ py: 1.25, width: 36 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: m.status === 'completed' ? '#f0fdf4' : '#f8fafc',
                            border: `1.5px solid ${cfg.border}`, color: cfg.color, fontWeight: 800, fontSize: '0.78rem' }}>
                            {m.status === 'completed' ? '✓' : m.milestoneNumber}
                          </Box>
                        </TableCell>

                        {/* Title */}
                        <TableCell sx={{ py: 1.25, maxWidth: 200 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#0f172a',
                            textDecoration: m.status === 'completed' ? 'line-through' : 'none',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>
                            {decodeHtml(m.title)}
                          </Typography>
                          {isOverdue && <Typography sx={{ fontSize: '0.65rem', color: '#dc2626', fontWeight: 600 }}>⚠ Overdue</Typography>}
                        </TableCell>

                        {/* Assigned */}
                        <TableCell sx={{ py: 1.25 }}>
                          {m.assignee ? (
                            <Tooltip title={m.assignee.name}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', background: '#2563eb' }}>
                                {m.assignee.name?.charAt(0).toUpperCase()}
                              </Avatar>
                            </Tooltip>
                          ) : <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>—</Typography>}
                        </TableCell>

                        {/* Dates */}
                        <TableCell sx={{ py: 1.25, fontSize: '0.72rem', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(m.startDate)}</TableCell>
                        <TableCell sx={{ py: 1.25, fontSize: '0.72rem', color: isOverdue ? '#dc2626' : '#64748b', fontWeight: isOverdue ? 700 : 400, whiteSpace: 'nowrap' }}>{fmtDate(m.endDate)}</TableCell>

                        {/* Progress */}
                        <TableCell sx={{ py: 1.25, width: 100 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={m.status === 'completed' ? 100 : m.completionPct || 0}
                              sx={{ flex: 1, height: 5, borderRadius: 3, background: '#f1f5f9',
                                '& .MuiLinearProgress-bar': { background: cfg.color, borderRadius: 3 } }} />
                            <Typography sx={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, minWidth: 28 }}>
                              {m.status === 'completed' ? 100 : m.completionPct || 0}%
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ py: 1.25 }}><StatusChip status={m.status} /></TableCell>

                        {/* Doc */}
                        <TableCell sx={{ py: 1.25 }}>
                          {m.documentPath ? (
                            <Tooltip title={m.documentName || 'View document'}>
                              <IconButton size="small" component="a"
                                href={`${API_BASE_URL.replace('/api/v1', '')}/uploads/${m.documentPath}`}
                                target="_blank" onClick={e => e.stopPropagation()}
                                sx={{ color: '#2563eb', p: 0.25 }}>
                                <FileIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            isAdminOrManager || user?.role === 'supervisor' ? (
                              <Tooltip title="Upload document">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setUploadFile(null); setUploadDialog({ open: true, milestone: m }); }}
                                  sx={{ color: '#94a3b8', p: 0.25 }}>
                                  <UploadIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>—</Typography>
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell sx={{ py: 1.25 }} onClick={e => e.stopPropagation()}>
                          <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
                            {m.status !== 'completed' && (
                              <Tooltip title="Mark Complete">
                                <Button size="small" variant="contained"
                                  sx={{ minWidth: 0, px: 1, py: 0.25, borderRadius: '6px', fontSize: '0.65rem', fontWeight: 700,
                                    background: 'linear-gradient(135deg,#16a34a,#15803d)', textTransform: 'none', whiteSpace: 'nowrap' }}
                                  onClick={() => { setCompleteNotes(''); setCompleteDialog({ open: true, milestone: m }); }}>
                                  ✓ Done
                                </Button>
                              </Tooltip>
                            )}
                            {isAdminOrManager && (
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openEdit(m)} sx={{ color: '#64748b', p: 0.5 }}>
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isAdminOrManager && m.status === 'completed' && (
                              <Tooltip title="Revert">
                                <IconButton size="small" onClick={() => setRevertConfirm({ open: true, milestone: m })} sx={{ color: '#d97706', p: 0.5 }}>
                                  <RevertIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isAdminOrManager && (
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => setDeleteConfirm({ open: true, milestone: m })} sx={{ color: '#dc2626', p: 0.5 }}>
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton size="small" sx={{ color: '#94a3b8', p: 0.5 }}>
                              {isExpanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* ── Expanded row ── */}
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0, background: '#fafbfc', borderBottom: '2px solid #e2e8f0' }}>
                            <Collapse in={isExpanded}>
                              <Box sx={{ px: 3, py: 2 }}>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} md={6}>
                                    {m.description && (
                                      <Box sx={{ mb: 1.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>Description</Typography>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>{m.description}</Typography>
                                      </Box>
                                    )}
                                    {m.notes && (
                                      <Box sx={{ mb: 1.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>Notes</Typography>
                                        <Box sx={{ p: 1.5, background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                          <Typography sx={{ fontSize: '0.78rem', color: '#15803d' }}>{m.notes}</Typography>
                                        </Box>
                                      </Box>
                                    )}
                                    {m.delayReason && (
                                      <Box sx={{ p: 1.5, background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: '#dc2626', fontWeight: 700 }}>
                                          ⚠ Delay: {DELAY_REASONS.find(r => r.value === m.delayReason)?.label}
                                        </Typography>
                                        {m.delayNotes && <Typography sx={{ fontSize: '0.72rem', color: '#dc2626', mt: 0.5 }}>{m.delayNotes}</Typography>}
                                      </Box>
                                    )}
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                      {m.completedDate && (
                                        <Box>
                                          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Completed On</Typography>
                                          <Typography sx={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 600 }}>{fmtDate(m.completedDate)}</Typography>
                                        </Box>
                                      )}
                                      {m.completer && (
                                        <Box>
                                          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Completed By</Typography>
                                          <Typography sx={{ fontSize: '0.82rem', color: '#0f172a', fontWeight: 600 }}>{m.completer.name}</Typography>
                                        </Box>
                                      )}
                                      {m.documentPath && (
                                        <Box>
                                          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>Document</Typography>
                                          <Button size="small" startIcon={<FileIcon />} component="a"
                                            href={`${API_BASE_URL.replace('/api/v1', '')}/uploads/${m.documentPath}`}
                                            target="_blank"
                                            sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, color: '#2563eb', p: 0 }}>
                                            {m.documentName || 'View file'}
                                          </Button>
                                        </Box>
                                      )}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
                {milestones.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: '#94a3b8' }}>No milestones yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ════ DIALOGS ════ */}

      {/* Add / Edit dialog */}
      {[{ open: addDialog, isAdd: true }, { open: editDialog.open, isAdd: false }].map(({ open, isAdd }) => (
        <Dialog key={isAdd ? 'add' : 'edit'} open={open}
          onClose={() => isAdd ? setAddDialog(false) : setEditDialog({ open: false, milestone: null })}
          maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
          <DialogTitle sx={{ fontWeight: 800, fontSize: '1.05rem', pb: 0.5 }}>
            {isAdd ? '+ Add Milestone' : '✏️ Edit Milestone'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>
              <TextField label="Title *" fullWidth value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
              <TextField label="Description" multiline rows={2} fullWidth value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Start Date" type="date" fullWidth value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="End Date" type="date" fullWidth value={form.endDate}
                    onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                </Grid>
              </Grid>
              {siteUsers.length > 0 && (
                <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                  <InputLabel>Assign To</InputLabel>
                  <Select value={form.assignedTo} label="Assign To" onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))}>
                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                    {siteUsers.map(u => <MenuItem key={u.id} value={u.id}>{u.name} ({u.role})</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              {!isAdd && (
                <>
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={form.status} label="Status" onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                      {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>Progress: {form.completionPct}%</Typography>
                    <input type="range" min={0} max={100} step={5} value={form.completionPct}
                      onChange={e => setForm(f => ({ ...f, completionPct: parseInt(e.target.value) }))}
                      style={{ width: '100%', accentColor: '#2563eb' }} />
                  </Box>
                  <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                    <InputLabel>Delay Reason</InputLabel>
                    <Select value={form.delayReason} label="Delay Reason" onChange={e => setForm(f => ({ ...f, delayReason: e.target.value }))}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {DELAY_REASONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  {form.delayReason && (
                    <TextField label="Delay Notes" multiline rows={2} fullWidth value={form.delayNotes}
                      onChange={e => setForm(f => ({ ...f, delayNotes: e.target.value }))}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
                  )}
                </>
              )}
              <TextField label="Notes" multiline rows={2} fullWidth value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={() => isAdd ? setAddDialog(false) : setEditDialog({ open: false, milestone: null })}
              sx={{ textTransform: 'none', borderRadius: '8px', color: '#64748b' }}>Cancel</Button>
            <Button variant="contained" onClick={isAdd ? handleAdd : handleEdit} disabled={saving || !form.title.trim()}
              sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700,
                background: 'linear-gradient(135deg,#0f172a,#1e293b)' }}>
              {saving ? 'Saving...' : isAdd ? 'Add Milestone' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      ))}

      {/* Complete dialog */}
      <Dialog open={completeDialog.open} onClose={() => setCompleteDialog({ open: false, milestone: null })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.05rem' }}>✅ Mark Complete</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: '#475569', mb: 2 }}>
            <strong>{completeDialog.milestone?.milestoneNumber}. {completeDialog.milestone?.title}</strong>
          </Typography>
          <TextField label="Completion Notes (optional)" multiline rows={3} fullWidth
            value={completeNotes} onChange={e => setCompleteNotes(e.target.value)}
            placeholder="Observations, handover notes..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setCompleteDialog({ open: false, milestone: null })}
            sx={{ textTransform: 'none', borderRadius: '8px', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleComplete} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, background: 'linear-gradient(135deg,#16a34a,#15803d)' }}>
            {saving ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload dialog */}
      <Dialog open={uploadDialog.open} onClose={() => setUploadDialog({ open: false, milestone: null })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1.05rem' }}>📎 Upload Document</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.82rem', color: '#475569', mb: 2 }}>
            {uploadDialog.milestone?.title}
          </Typography>
          <Box sx={{ border: '2px dashed #e2e8f0', borderRadius: '12px', p: 3, textAlign: 'center', cursor: 'pointer',
            '&:hover': { borderColor: '#2563eb', background: '#f8faff' } }}
            onClick={() => fileInputRef.current?.click()}>
            <UploadIcon sx={{ fontSize: 32, color: '#94a3b8', mb: 1 }} />
            <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>
              {uploadFile ? uploadFile.name : 'Click to select file (PDF, image, max 10MB)'}
            </Typography>
            <input ref={fileInputRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
              onChange={e => setUploadFile(e.target.files[0])} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setUploadDialog({ open: false, milestone: null })}
            sx={{ textTransform: 'none', borderRadius: '8px', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={saving || !uploadFile}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700, background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' }}>
            {saving ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, milestone: null })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#dc2626' }}>🗑 Delete Milestone?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.88rem', color: '#475569' }}>
            Delete <strong>{deleteConfirm.milestone?.title}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, milestone: null })}
            sx={{ textTransform: 'none', borderRadius: '8px', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700 }}>
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert confirm */}
      <Dialog open={revertConfirm.open} onClose={() => setRevertConfirm({ open: false, milestone: null })}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#d97706' }}>↩ Revert Milestone?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.88rem', color: '#475569' }}>
            Revert <strong>{revertConfirm.milestone?.title}</strong> from Completed to In Progress?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setRevertConfirm({ open: false, milestone: null })}
            sx={{ textTransform: 'none', borderRadius: '8px', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRevert} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700 }}>
            {saving ? 'Reverting...' : 'Revert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset confirm */}
      <Dialog open={resetConfirm} onClose={() => setResetConfirm(false)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#dc2626' }}>⚠️ Reset All Milestones?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.88rem', color: '#475569' }}>
            This deletes all milestone progress and restores the 10-step EV Bus Depot template. Cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setResetConfirm(false)}
            sx={{ textTransform: 'none', borderRadius: '8px', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReset} disabled={saving}
            sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700 }}>
            {saving ? 'Resetting...' : 'Reset All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MilestonesTab;